import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { path, prop, propOr } from 'ramda';
import styled from 'styled-components';
import config from '../../../configs/config';
import BuildingIcon from '../../../images/building.svg';
import JoinCompassImg from '../../../images/join-compass.svg';
import maybeComponent from '../../../utils/maybe-component';
import { Column, Row } from '../common-components';
import Map from '../map';
import QScore from '../q-score';
import messages from './messages';

export const getAddress = address =>
  `${path(['location', 'city'], address)}, ${path(
    ['location', 'province'],
    address
  )}`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  margin: 0 20%;
`;

const SubLogo = styled.img`
  margin-top: -5rem;
`;

const SubDetails = styled(Row)`
  > * {
    margin-right: 1.25rem;
  }
`;

const ButtonGroup = styled(Row)`
  > :first-child {
    margin-right: 1rem;
  }
`;

const Button = styled.button`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ success }) => (success ? 'white' : '#484851')};
  padding: 0.5rem 1rem;
  background-color: ${({ success }) => (success ? '#028a0f' : '#e0e0e3')};
  border: none;
  cursor: pointer;
  text-decoration: none;
  border-radius: 0.25rem;
  &: hover {
    background-color: ${({ success }) => (success ? '#028a0f' : '#bdbdbd')};
  }
`;

const LeftSection = styled(Column)`
  min-width: 40vw;
  margin-right: 1.5rem;
  > * {
    margin-bottom: 2rem;
  }
`;

const RightSection = styled(Column)`
  > * {
    margin-bottom: 2rem;
  }
`;

const CardContainer = styled.div`
  width: 100%;
  border: 0.1rem solid #f2f2f3;
  padding: 1.5rem 1.5rem 2rem 2rem;
  border-radius: 0.25rem;
  > h3 {
    margin-bottom: 0.5rem;
  }
`;

const Card = ({ heading, children }) => (
  <CardContainer>
    <h3>
      <FormattedMessage {...heading} />
    </h3>
    {children}
  </CardContainer>
);

const Details = styled(Row)`
  align-items: flex-start;
  margin: 2rem 0;
`;

const Text = styled.div`
  color: #37373d;
`;

const TradeTile = styled.div`
  color: ${({ isPrimary }) => (isPrimary ? 'white' : '#66666F')};
  background-color: ${({ isPrimary }) => (isPrimary ? '#9797A0' : '#F2F2F3')};
  padding: 0.25rem 0.5rem;
  font-weight: 700;
  border-radius: 0.25rem;
`;

const Trades = styled(Row)`
  justify-content: flex-start;
  > * {
    margin-right: 1.25rem;
  }
`;

const ComanyDetails = styled(Column)`
  align-items: flex-start;
  margin-left: 1rem;
`;

const ShareProfile = () => <FormattedMessage {...messages.shareProfile} />;

const LinkCopied = () => <FormattedMessage {...messages.linkCopied} />;

const MaybeShareProfileMessage = maybeComponent(LinkCopied)(ShareProfile);

const Profile = ({ profile }) => {
  const [shareProfile, setShareProfile] = useState(false);

  useEffect(() => {
    if (!shareProfile) setTimeout(() => setShareProfile(false), 2000);
  }, [shareProfile]);

  return (
    <Wrapper>
      <Row fullWidth={true} justify="space-between" wrap>
        <Row wrap>
          <SubLogo src={BuildingIcon} alt="Sublogo" />
          <ComanyDetails>
            <h2>{prop('company_name', profile)}</h2>
            <SubDetails wrap>
              <div>{getAddress(prop('primary_office', profile))}</div>
              <a href={`https://${profile.url}`}>{profile.url}</a>
            </SubDetails>
          </ComanyDetails>
        </Row>
        <ButtonGroup>
          <Button
            success={shareProfile}
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setShareProfile(true);
            }}
          >
            <MaybeShareProfileMessage condition={shareProfile} />
          </Button>
          <Button>
            <FormattedMessage {...messages.viewInCompass} />
          </Button>
        </ButtonGroup>
      </Row>
      <Details>
        <LeftSection>
          <Card heading={messages.companyOverview}>
            <Text>{profile.details}</Text>
          </Card>
          <Card heading={messages.trades}>
            <Trades>
              <TradeTile isPrimary>
                {path(['primary_trade', 'name'], profile)}
              </TradeTile>
              {propOr([], 'secondary_trades', profile).map(trade => (
                <TradeTile key={trade.name}>{prop('name', trade)}</TradeTile>
              ))}
              <TradeTile>
                <Text>{path(['primary_trade', 'name'], profile)}</Text>
              </TradeTile>
            </Trades>
          </Card>
          <Card heading={messages.officeLocation}>
            <Map
              lat={path(
                ['primary_office', 'location', 'location', 'lat'],
                profile
              )}
              lng={path(
                ['primary_office', 'location', 'location', 'lng'],
                profile
              )}
              zoom={15}
              secret={prop('googleSecretKey', config)}
              address={path(['primary_office', 'address'], profile)}
            />
          </Card>
        </LeftSection>
        <RightSection>
          <Card heading={messages.qScore}>
            <QScore score={profile.score} />
          </Card>
          <img src={JoinCompassImg} alt={'Join Compass'} />
        </RightSection>
      </Details>
    </Wrapper>
  );
};

export default Profile;
